/**
 * Displacement ceremony — client surface for admit_with_displacement (D2).
 *
 * PRD: docs/9-exploration/DISPLACEMENT-AND-COMMITMENT-STATES-PRD.md §5.
 * Schema + RPC: supabase/migrations/701_displacement_ceremony.sql.
 * Pool math: src/lib/poolPressure.ts. Per-commitment state: commitmentState.ts.
 *
 * One transactional call (Build Contract §2.3a) — never client-side stitching.
 * The engine's numbers travel as the STATED basis; the invariant that a
 * shortfall is answered is enforced server-side, where `resolved_hours` is
 * derived from the selections rather than taken from here.
 *
 * NOTE ON TYPING: this RPC ships with migration 701, which is written but not
 * applied to any environment, so it is absent from database.generated.ts. The
 * call therefore goes through the untyped client on purpose. Once the
 * integrator applies 701 and `npm run db:types` regenerates, this should move
 * to getTypedSupabaseClient() like the rest of the claim surface.
 */

import { getSupabaseClient } from './supabaseClient';
import type { AdmissionImpact } from './poolPressure';
import type { ResourceRequirementLine } from './commitmentState';

export type DisplacementDisposition = 'delay' | 'reduce' | 'cancel' | 'add_resources';

export const DISPOSITION_LABELS: Record<DisplacementDisposition, string> = {
  delay: 'Delay it',
  reduce: 'Reduce it',
  cancel: 'Cancel it',
  add_resources: 'Add resources',
};

/**
 * What each disposition means for the record, in the words the ceremony shows.
 * `reduce` and `cancel` change claims and so free a number the record can
 * compute; `delay` and `add_resources` are promises the record can only quote.
 */
export const DISPOSITION_HELP: Record<DisplacementDisposition, string> = {
  delay: 'Its work moves out of this cycle. The hours it frees are your estimate — nothing on the record can compute them.',
  reduce: 'Its resource claim is replaced by a smaller one. The hours freed are computed from the two claims.',
  cancel: 'Its resource claims are withdrawn from the record with your stated reason. The hours freed are computed from what was withdrawn.',
  add_resources: 'The pool grows instead of something coming off. The hours are your estimate until capacity actually changes.',
};

export interface DisplacementSelectionInput {
  displacedItemId: string;
  disposition: DisplacementDisposition;
  resourceType?: string | null;
  /** Used for delay / add_resources only; derived server-side otherwise. */
  hoursFreed?: number;
  basis?: string;
  /** reduce: the claim being replaced. */
  supersedeClaimId?: string;
  /** reduce: the smaller number of hours that remain. */
  replacementHours?: number;
  /** Links the counterpart's answer on the existing participation machinery. */
  acknowledgmentParticipationId?: string;
}

export interface AdmissionClaimInput {
  claimKind: 'capacity_hours' | 'funding';
  resourceType?: string | null;
  resourceName?: string | null;
  declaredHours?: number;
  declaredAmount?: number;
  currency?: string;
  fundingEnvelopeId?: string;
  periodStart?: string;
  periodEnd?: string;
  basis?: string;
}

export interface AdmitWithDisplacementInput {
  planCycleId: string;
  admittedItemId: string;
  /** The engine's numbers, recorded as the basis the decision was made on. */
  marginalRequirement: readonly ResourceRequirementLine[];
  impact: AdmissionImpact;
  selections: readonly DisplacementSelectionInput[];
  claims?: readonly AdmissionClaimInput[];
  acceptOvercommitment?: boolean;
  acceptBasis?: string;
  basis?: string;
  decisionId?: string;
}

export interface AdmitWithDisplacementResult {
  ok: boolean;
  displacementRecordId?: string;
  error?: string;
}

export async function admitWithDisplacement(
  input: AdmitWithDisplacementInput
): Promise<AdmitWithDisplacementResult> {
  const supabase = getSupabaseClient();
  if (!supabase) return { ok: false, error: 'Supabase client unavailable' };

  const { data, error } = await supabase.rpc('admit_with_displacement', {
    p_plan_cycle_id: input.planCycleId,
    p_admitted_item_id: input.admittedItemId,
    p_marginal_requirement: input.marginalRequirement.map((r) => ({
      resourceType: r.resourceType,
      hours: r.hours,
    })),
    p_pool_pressure: input.impact.pools.map((p) => ({
      resourceType: p.resourceType,
      capacityHours: p.capacityHours,
      thresholdHours: p.thresholdHours,
      requiredBeforeHours: p.requiredBeforeHours,
      marginalHours: p.marginalHours,
      requiredAfterHours: p.requiredAfterHours,
      shortfallHours: p.shortfallHours,
      alreadyOver: p.alreadyOver,
      noCapacityOnFile: p.noCapacityOnFile,
    })),
    p_shortfall_hours: input.impact.shortfallHours,
    p_selections: input.selections.map((s) => ({
      displaced_item_id: s.displacedItemId,
      disposition: s.disposition,
      resource_type: s.resourceType ?? null,
      hours_freed: s.hoursFreed ?? 0,
      basis: s.basis ?? null,
      supersede_claim_id: s.supersedeClaimId ?? null,
      replacement_hours: s.replacementHours ?? null,
      acknowledgment_participation_id: s.acknowledgmentParticipationId ?? null,
    })),
    p_claims: (input.claims ?? []).map((c) => ({
      claim_kind: c.claimKind,
      resource_type: c.resourceType ?? null,
      resource_name: c.resourceName ?? null,
      declared_hours: c.declaredHours ?? null,
      declared_amount: c.declaredAmount ?? null,
      currency: c.currency ?? null,
      funding_envelope_id: c.fundingEnvelopeId ?? null,
      period_start: c.periodStart ?? null,
      period_end: c.periodEnd ?? null,
      basis: c.basis ?? null,
    })),
    p_accept_overcommitment: input.acceptOvercommitment ?? false,
    p_accept_basis: input.acceptBasis ?? null,
    p_basis: input.basis ?? null,
    p_decision_id: input.decisionId ?? null,
  });

  if (error) return { ok: false, error: error.message };
  return { ok: true, displacementRecordId: typeof data === 'string' ? data : undefined };
}

/**
 * Hours a selection frees, as the CEREMONY can know them before it is sent.
 * Mirrors the server's rule exactly: reduce and cancel are computed from
 * claims; delay and add_resources are whatever the person stated. The server
 * recomputes the first two and ignores this — the tally on screen must never
 * be able to disagree with the tally that decides.
 */
export function selectionHoursFreed(
  selection: DisplacementSelectionInput,
  liveClaimHours: { byClaimId: Map<string, number>; byItemId: Map<string, number> }
): number {
  if (selection.disposition === 'reduce') {
    const old = selection.supersedeClaimId
      ? liveClaimHours.byClaimId.get(selection.supersedeClaimId)
      : undefined;
    if (old == null || selection.replacementHours == null) return 0;
    return Math.max(0, old - selection.replacementHours);
  }
  if (selection.disposition === 'cancel') {
    return liveClaimHours.byItemId.get(selection.displacedItemId) ?? 0;
  }
  return Math.max(0, selection.hoursFreed ?? 0);
}

/**
 * Whether a cycle has adopted the displacement discipline
 * (`plan_cycles.displacement_discipline`, migration 701). Returns false when
 * the column is not there yet — the migration is written but unapplied, and a
 * missing column must read as "this cycle has not adopted it", never as an
 * error the user sees.
 */
export async function cycleHasDisplacementDiscipline(
  planCycleId: string | null | undefined
): Promise<boolean> {
  if (!planCycleId) return false;
  const supabase = getSupabaseClient();
  if (!supabase) return false;
  const { data, error } = await supabase
    .from('plan_cycles')
    .select('displacement_discipline')
    .eq('id', planCycleId)
    .maybeSingle();
  if (error || !data) return false;
  return (data as { displacement_discipline?: boolean }).displacement_discipline === true;
}

/**
 * Whether "what comes off?" has already been answered for this commitment in
 * this plan cycle — a plan_displacement_records row for the admitted item
 * (701). Used by the Place-a-commitment flow to offer the ceremony only while
 * it is still unanswered. Fails closed to false (the offer stays visible).
 */
export async function displacementAnsweredForItem(
  planCycleId: string | null | undefined,
  itemId: string | null | undefined
): Promise<boolean> {
  if (!planCycleId || !itemId) return false;
  const supabase = getSupabaseClient();
  if (!supabase) return false;
  const { data, error } = await supabase
    .from('plan_displacement_records')
    .select('id')
    .eq('plan_cycle_id', planCycleId)
    .eq('admitted_item_id', itemId)
    .limit(1);
  if (error || !data) return false;
  return data.length > 0;
}

/**
 * Where the ceremony is asked: the Above / Below the Line view, opened on one
 * commitment. The view needs the engine's numbers (marginal requirement, pool
 * pressure) that only a run of a scenario linked to this plan cycle has.
 */
export function displacementCeremonyHref(planCycleId: string, itemId: string): string {
  const q = new URLSearchParams({ admit: itemId, cycle: planCycleId });
  return `/planning/engine/above-below?${q.toString()}`;
}
